import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Loader2, X, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useExam } from '../contexts/ExamContext';
import { BOTON_VENTANA_PRINCIPAL, BOTON_VENTANA_SECUNDARIO } from './panel/VentanaDelPanel';
import { useHojaDeLaCasa, FilaDeAgarradera, ENTRA_EL_VELO, ENTRA_LA_CAJA } from './alumno/arrastreDeHoja';
import AvisoAlUnirse from './AvisoAlUnirse';
import { AVISO_PRIVACIDAD_VERSION } from '../lib/avisoPrivacidad';
import {
  readPendingJoinCode,
  clearPendingJoinCode,
  olvidarCodigoUnido,
  markPendingJoinPrompted,
  wasPendingJoinPrompted,
  clearPendingJoinPrompted,
  abrirVueloDelEmpujon,
  cerrarVueloDelEmpujon,
  copyDeFalloDeGrupo,
  descartarEmpujon,
  fueDescartadoElEmpujon,
  vueloDelEmpujon,
} from '../lib/groups';

/* PILOTO-1B (firma #80) — EL ÚLTIMO EMPUJÓN: «te está esperando tu grupo».
 *
 * EL HUECO QUE CIERRA. Hasta PILOTO-1, el alumno que abría `/join/CODIGO` sin
 * sesión veía el código, entraba o creaba cuenta… y aterrizaba en el dashboard
 * con el código guardado pero SIN que nadie se lo recordara. El respaldo era la
 * tarjeta de Ajustes pre-llenada — que sigue existiendo, intacta, pero exige que
 * el alumno sepa ir a buscarla. Esto lo trae al frente una vez.
 *
 * DÓNDE VIVE Y POR QUÉ AHÍ. Montado en `AppLayout` (App.js), o sea AGUAS ABAJO
 * del login. Esto NO toca ni una línea del flujo de autenticación —ni /login, ni
 * signup, ni routes/auth.py—, que es la misma decisión firmada en PILOTO-1 §2.6:
 * el camino por el que pasa el 100% de los usuarios no se modifica para servir a
 * un caso del piloto. El modal se entera de que hubo un aterrizaje porque el
 * chasis se monta; no necesita que el login le avise.
 *
 * UNA SOLA VEZ POR ATERRIZAJE. La marca vive en `sessionStorage`
 * (lib/groups.js) y se pone ANTES de preguntarle al servidor: lo que se gasta
 * por aterrizaje es UNA consulta, no una por pantalla — y ese cubo
 * (`group_join_user`, 20/h) es la defensa del código, no un presupuesto que
 * convenga quemar en reintentos.
 *
 * ── PILOTO-1C (firma #81) — LO QUE ESTE DOCBLOCK DECÍA MAL ───────────────────
 *
 * Aquí decía que «`AppLayout` se re-monta en cada cambio de pantalla». ES FALSO,
 * y medido: React Router reconcilia por tipo y posición, así que el chasis se
 * monta UNA VEZ POR CARGA DE PÁGINA y sobrevive a todas las navegaciones. Sobre
 * esa creencia se apoyaban dos cosas del código:
 *
 *   · la marca, «para que el modal no salga en cada navegación» — no hacía
 *     falta para eso; hace falta para sobrevivir a un F5, que es otra cosa;
 *   · el reintento tras un fallo sin veredicto, «el siguiente montaje
 *     reintenta» — ese montaje no llegaba nunca dentro de la carga.
 *
 * Resultado: UN intento por carga, y cualquier final que no pintara el modal se
 * llevaba el empujón entero en silencio. Se cierra por los dos lados:
 *
 *   1. EL VUELO SE ADOPTA (lib/groups.js). La consulta vive en memoria de
 *      módulo, así que un montaje nuevo hereda la respuesta en vez de perderla,
 *      y la doble corrida de StrictMode adopta la promesa de la primera —una
 *      sola petición, sin depender de que no haya descarte por limpieza.
 *   2. EL EFECTO VUELVE A CORRER AL CAMBIAR DE PANTALLA (`pathname` en las
 *      dependencias). Es el «siguiente montaje» que el código ya prometía y no
 *      tenía. En el camino feliz no cuesta ni una petición más: la marca ya
 *      está puesta y el efecto sale por ella.
 *
 * ── PILOTO-1D (firma #82) — CERRAR CON LA X ES DEFINITIVO ────────────────────
 *
 * ANTES: cerrar valía para esa CARGA de página. Tras un F5 el modal volvía a
 *        salir, y en una pestaña nueva también.
 * AHORA: quien cierra no vuelve a ver ESE ofrecimiento jamás — ni al navegar,
 *        ni tras F5, ni al cerrar la pestaña y abrir otra. El descarte vive en
 *        `localStorage` y va por CÓDIGO (lib/groups.js).
 * POR QUÉ: decisión firmada por Carlos tras el smoke. Un modal a pantalla
 *        completa que reaparece cada vez que el alumno abre la app deja de ser
 *        un empujón y se vuelve un obstáculo; y el respaldo sigue entero, que es
 *        lo que hace barata la decisión: el código queda pre-llenado en Ajustes
 *        y unirse son dos toques cuando el alumno quiera.
 * Y SI EL PROFE REGENERA, el link nuevo trae código nuevo, el descarte viejo no
 *        aplica y el modal vuelve a ofrecer. Correcto: es otra invitación.
 * LOS DOS BOTONES DE CERRAR SON EL MISMO GESTO. La X y «Ahora no» pasan por
 *        `cerrar`, así que los dos descartan para siempre: cerrar sin unirse es
 *        una sola decisión, no dos con reglas distintas.
 *
 * CERRAR NO BORRA EL RESPALDO. Cerrar dice «esto no» y nada más: el código
 * pendiente SIGUE guardado y la tarjeta de Ajustes sigue saliendo pre-llenada.
 * Lo que borra el pendiente es un FINAL, y los finales son estos cuatro: unirse (ya
 * entró), un veredicto de que el código está muerto (vencido o inválido),
 * descubrir que el alumno YA ES MIEMBRO (PILOTO-1C: es un final aunque no haya
 * tocado nada, y sin él el empujón le ofrecía en cada aterrizaje un grupo en el
 * que ya está) y el 400 del profesor que abre su propio link. Ninguno de los
 * cuatro es «cerrar».
 *
 * QUÉ NO CUENTA COMO VEREDICTO: un fallo de red, un 5xx, un 429 o un rechazo de
 * sesión no dicen NADA sobre el código, así que no lo borran y no pintan un
 * mensaje que sería mentira. Simplemente no hay modal en ese aterrizaje y el
 * respaldo de Ajustes queda entero. Es la misma frontera que `lib/api.js` ya usa
 * para no confundir «no hay señal» con «el dato es malo».
 *
 * QUIÉN LO VE: cualquier cuenta REGISTRADA, con bandera de profesor o sin ella
 * (un profe puede ser alumno de otro). Un guest NO: la ruta ya le contesta 403,
 * así que preguntar sería gastar una petición para pintarle un error.
 *
 * SAFE AREAS: es `fixed` al viewport y NO hereda las del chasis, así que escribe
 * la suya abajo — mismo patrón que los modales de PILOTO-1 (Dashboard.js:2994).
 * Tap targets de 44 pt en todo lo que se toca, incluida la X.
 */

// Lo único que borra el código pendiente al fallar: un veredicto sobre el CÓDIGO.
// 410 = vencido (services/groups.py) · 404 = no existe.
const VEREDICTO_SOBRE_EL_CODIGO = { 410: 'expired', 404: 'invalid' };

// LOS QUE NO DICEN NADA DEL CÓDIGO PERO SÍ DICEN «DEJA DE PREGUNTAR». No borran
// el código pendiente —sigue siendo bueno— pero SÍ consumen el turno del
// empujón, o sea que no se reintenta en esta sesión:
//
//   · 429 — el freno de peticiones. Es literalmente «estás preguntando de más»;
//     soltar el turno y volver a consultar sería desobedecerlo, y ese cubo
//     (`group_join_user`, 20/h) es la defensa del código de ingreso.
//   · 403 — la sesión no puede usar grupos. Reintentar no la va a convencer.
//
// TODO LO DEMÁS —sin respuesta (red caída, timeout), 5xx— es un tropiezo que no
// dice nada de nada: ahí el turno se DEVUELVE y el siguiente montaje reintenta.
const CALLA_SIN_REINTENTO = [429, 403];

export default function PendingGroupJoinModal() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user, loading } = useAuth();
  // EL ÚNICO USO DEL ROUTER AQUÍ, y es el disparador del reintento: cambiar de
  // pantalla es lo más parecido a «otro montaje» que hay dentro de una carga de
  // página (ver el docblock). No navega, no lee parámetros: sólo se entera.
  const { pathname } = useLocation();
  // EL CANDADO DE EXAMEN, y no es una cortesía. Este es el TERCER componente que
  // el chasis monta junto a `Sidebar` y `MobileBottomNav`, y los otros dos ya
  // consultan este mismo estado; `/settings` está incluso duro-bloqueado por
  // `ExamLockedRoute` con la razón firmada en T1-C3 («con examen propio en vuelo
  // no se entra a Ajustes por ninguna vía»). Las tres pantallas de estudio viven
  // dentro de este mismo `AppLayout`, así que sin esta consulta un velo negro a
  // pantalla completa podía caerle encima a un examen con el reloj corriendo —
  // por un código que quedó en `localStorage` hace dos días.
  //
  // NO CONSUME EL TURNO: el examen no dice nada del código pendiente. Cuando el
  // examen termina, `isExamActive` cambia, el efecto vuelve a correr y el
  // empujón sale entonces, que es cuando el alumno puede atenderlo.
  const { isExamActive } = useExam();

  // idle → (nada que pintar) · ready → la invitación · joining → en vuelo
  // expired / invalid → el mensaje amable y final
  const [estado, setEstado] = useState('idle');
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);       // {name, owner_name}
  const [yaMiembro, setYaMiembro] = useState(false);

  const registrado = Boolean(user) && !user.is_guest;

  // CERRAR SE ANOTA FUERA DEL COMPONENTE (PILOTO-1C). Con el vuelo adoptable, un
  // estado local no basta: cualquier re-corrida del efecto volvería a pintarle
  // encima el modal que el alumno acaba de cerrar.
  //
  // PILOTO-1D — Y LA NOTA VA POR CÓDIGO, Y ES PARA SIEMPRE. Antes era una
  // bandera de módulo que moría con la carga de página, así que el mismo
  // ofrecimiento volvía en la siguiente pestaña. Decisión firmada por Carlos:
  // quien cierra con la X no vuelve a ver ESE ofrecimiento jamás; el respaldo
  // que queda es el código pre-llenado en Ajustes, que esta función no toca.
  const cerrar = useCallback(() => { descartarEmpujon(code); setEstado('idle'); }, [code]);

  // QUITAR EL MODAL DE ENMEDIO **SIN** DESCARTAR NADA, y la diferencia importa
  // desde PILOTO-1D. `cerrar` es el GESTO DEL ALUMNO —la X y «Ahora no»— y ahora
  // es para siempre; `ocultar` es lo que hacen los caminos que ya terminaron por
  // otro lado: se unió, ya era miembro, el código resultó muerto, o el intento
  // falló por algo transitorio. Con una sola función, unirse dejaba escrito un
  // descarte de un código al que el alumno acababa de ENTRAR, y —peor— un
  // «grupo lleno» o una red caída le habrían silenciado la invitación para
  // siempre. Un tropiezo no es una decisión.
  const ocultar = useCallback(() => setEstado('idle'), []);

  // El final feliz y los dos finales muertos comparten una cosa: el código
  // pendiente deja de existir —y con él, el recuerdo de haberlo descartado.
  // Cerrar con la X NO pasa por aquí.
  const soltarPendiente = useCallback(() => olvidarCodigoUnido(code), [code]);

  // PINTAR LA RESPUESTA, venga de una consulta propia o de un vuelo ADOPTADO.
  // Es la misma función en los dos casos a propósito: si el montaje que preguntó
  // y el que hereda pintaran por caminos distintos, el segundo se desviaría del
  // primero en el primer retoque.
  //
  // LA MARCA SE SUELTA SI LA CORRIDA NO LLEGÓ A SABER NADA. Se pone ANTES de la
  // consulta —para que dos montajes no gasten dos peticiones del cubo
  // `group_join_user`, que es la defensa del código— pero un fallo que NO es
  // veredicto NI un «deja de preguntar» (red caída, timeout, 5xx) la libera, y
  // con ella el vuelo: esa corrida no supo nada del código, así que no puede
  // consumir el empujón. Es la receta que el repo ya firmó en
  // `useAutoSplitRestore`: lo que hace final al guard es una corrida COMPLETA.
  // El 429 y el 403 son la excepción y tienen su porqué en `CALLA_SIN_REINTENTO`.
  const adoptar = useCallback((promesa) => {
    promesa.then((res) => {
      setPreview(res.data.group);
      const miembro = Boolean(res.data.already_member);
      setYaMiembro(miembro);
      // PILOTO-1C — YA-MIEMBRO SUELTA EL CÓDIGO EN ESTE INSTANTE, la misma regla
      // que la pantalla de `/join`. Antes sólo lo soltaba el botón «Entendido»:
      // quien cerraba con la X se llevaba el código intacto y el empujón le
      // volvía a ofrecer, aterrizaje tras aterrizaje, un grupo en el que YA está.
      // Ser miembro es un final, y los finales sueltan el pendiente.
      if (miembro) clearPendingJoinCode();
      setEstado('ready');
    }).catch((err) => {
      const status = err.response?.status;
      const veredicto = VEREDICTO_SOBRE_EL_CODIGO[status];
      if (!veredicto) {
        if (!CALLA_SIN_REINTENTO.includes(status)) {
          cerrarVueloDelEmpujon();
          clearPendingJoinPrompted();
        }
        return;
      }
      clearPendingJoinCode();
      setEstado(veredicto);
    });
  }, []);

  // ── EL EFECTO ──────────────────────────────────────────────────────────────
  //
  // NO HAY DESCARTE POR LIMPIEZA, y sigue siendo load-bearing: `index.js` monta
  // la app dentro de `<React.StrictMode>`, que en desarrollo hace montar →
  // limpiar → montar cada efecto. Antes eso se sostenía porque la corrida #2
  // salía por la marca y la #1 alcanzaba a escribir su estado; ahora la #2
  // ADOPTA el vuelo de la #1, que es lo mismo pero sin depender de quién escribe
  // primero — y de paso es lo que salva al aterrizaje cuyo chasis muere con la
  // consulta en vuelo.
  useEffect(() => {
    if (loading || !registrado || isExamActive) return undefined;
    const pendiente = readPendingJoinCode();
    if (!pendiente) return undefined;
    // PILOTO-1D — EL DESCARTE SE PREGUNTA POR CÓDIGO, así que hay que leer el
    // pendiente PRIMERO (antes era una bandera global y el orden daba igual).
    // Cerrado con la X = cerrado para siempre: ni al navegar, ni tras un F5, ni
    // en una pestaña nueva. Y sin gastar una consulta en preguntarlo.
    //
    // Si el profesor REGENERA el código, lo que hay en `localStorage` es otro
    // código, el descarte viejo no aplica y el modal vuelve a ofrecer. Es
    // correcto: una invitación nueva merece que se pregunte de nuevo.
    if (fueDescartadoElEmpujon(pendiente)) return undefined;
    setCode(pendiente);

    // EL VUELO GANA A LA MARCA, y ése es todo el orden que importa: la marca la
    // puso el propio vuelo que estamos adoptando, así que preguntarle a ella
    // primero enterraría la respuesta que ya viene en camino.
    const enVuelo = vueloDelEmpujon(pendiente);
    if (enVuelo) { adoptar(enVuelo); return undefined; }

    // Sin vuelo vivo: la marca decide. Si está puesta, otra CARGA de página ya
    // gastó este turno (sobrevive a un F5) y aquí no se pregunta nada.
    if (wasPendingJoinPrompted()) return undefined;

    markPendingJoinPrompted();
    adoptar(abrirVueloDelEmpujon(
      pendiente, api.get(`/groups/lookup/${encodeURIComponent(pendiente)}`)));
    return undefined;
  }, [loading, registrado, isExamActive, pathname, adoptar]);

  const unirse = async () => {
    setEstado('joining');
    try {
      const res = await api.post('/groups/join', { code, aviso_version: AVISO_PRIVACIDAD_VERSION });
      // post-t7-2c (D4): el Panel de abajo vuelve a pedir el material de los grupos: la tarjeta, sin recargar.
      window.dispatchEvent(new CustomEvent('studybuddy:grupos-cambiados'));
      soltarPendiente();
      toast.success(res.data.already_member
        ? t('dashboard:groups.toasts.already_member', { name: res.data.group?.name })
        : t('dashboard:groups.toasts.joined', { name: res.data.group?.name }));
      ocultar();
    } catch (err) {
      const status = err.response?.status;
      const veredicto = VEREDICTO_SOBRE_EL_CODIGO[status];
      if (veredicto) {
        // El código murió entre la vista previa y el botón (el profe lo regeneró
        // justo ahí). Se le dice, y se suelta.
        clearPendingJoinCode();
        setEstado(veredicto);
        return;
      }
      if (status === 400) {
        // «You already own this group.» — el caso natural del PROFESOR que abre
        // su propio link para comprobar que sirve antes de repartirlo. Es un NO
        // PERMANENTE, no un tropiezo: sin soltar el código, el modal le salía en
        // cada carga de la app para siempre, gastando además una unidad de sus
        // dos cubos cada vez.
        clearPendingJoinCode();
      }
      // Grupo lleno, tope propio, red: el código sigue siendo bueno, así que se
      // conserva y el respaldo de Ajustes queda entero.
      //
      // PILOTO-1C — y el rechazo del SELLO de sesión (403) sale en cristiano en
      // vez de «Invalid CSRF token», que es lo que el smoke encontró. El copy lo
      // decide `copyDeFalloDeGrupo` por código de estado, igual en las seis
      // superficies de grupos.
      toast.error(copyDeFalloDeGrupo(err, t, 'dashboard:groups.toasts.join_failed'));
      ocultar();
    }
  };

  // Y si el examen arranca con el modal ya abierto (el alumno lo dejó ahí y tocó
  // «empezar»), tampoco se pinta: el candado manda mientras dure.
  // marca-1 remate (#738, firma de Carlos): deslizar la barrita (80 o un tiron) hace lo mismo que la X (descarta la
  // invitacion y cierra); tocar fuera sigue sin cerrarla; mientras se esta uniendo, la hoja regresa. Entra y sale
  // como las demas hojas de la casa.
  const hoja = useHojaDeLaCasa(cerrar, { abierta: estado !== 'idle' && !isExamActive, cerrable: estado !== 'joining' });

  if (estado === 'idle' || isExamActive) return null;

  const muerto = estado === 'expired' || estado === 'invalid';

  /* dashboard-rediseno (D5, "Te espera tu grupo"): LA VENTANA CON EL ESTILO DE LA SECCION H. En
     computadora y tablet, centrada, 440 px, fondo #161616, borde #2A2A2A, radio 20, relleno 24,
     sobre negro al 66 %; en el telefono, hoja desde abajo a lo ancho con radio 24 arriba, su
     agarradera y los botones a lo ancho con el principal ARRIBA. El nombre del grupo va en un
     recuadro; "Join" es el naranja. Lo que NO cambia, a proposito: la capa sigue debajo de la
     barra de arriba, como antes, sin portal, y SOLO la X y "Not now" cierran (cerrar aqui es para
     siempre, PILOTO-1D: un toque en el fondo no puede descartar una invitacion); los textos, las
     marcas y la conducta son los de antes. */
  return (
    <div
      ref={hoja.veloRef}
      className={`fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/[0.66] flex items-end md:items-center justify-center md:p-4 ${ENTRA_EL_VELO}`}
      data-testid="pending-join-modal">
      <div ref={hoja.cajaRef} className={`relative w-full md:max-w-[440px] bg-[#161616] border border-[#2A2A2A] rounded-t-[24px] md:rounded-[20px] px-6 pt-4 md:pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] md:pb-6 shadow-2xl max-h-full flex flex-col ${ENTRA_LA_CAJA}`}>
        <FilaDeAgarradera agarradera={hoja.agarradera} testid="pending-join-agarradera"
          clase="md:hidden relative flex justify-center mb-3" />
        <button
          type="button"
          onClick={cerrar}
          aria-label={t('common:close', 'Close')}
          className="absolute z-[2] top-3 right-3 min-h-[44px] min-w-[44px] rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:text-[#F4F4F3] active:text-[#F4F4F3] active:duration-0"
          data-testid="pending-join-close">
          <X className="w-5 h-5" />
        </button>

        {/* Quinta vuelta (D23, la misma regla en las ventanas del Panel): la hoja mide como mucho lo que
            deja la capa (debajo de la barra de arriba) y lo de adentro se desplaza; la X no se va con el.
            Sexta vuelta (seccion H: "los botones quedan fijos abajo"): los botones van en su pie, fuera de
            lo que se desplaza. */}
        <div className="flex-1 min-h-0 overflow-y-auto max-md:overscroll-contain max-md:touch-pan-y" data-testid="pending-join-cuerpo">
        <div className="w-11 h-11 rounded-xl bg-[#1C130D] flex items-center justify-center mb-4">
          <Users className="w-5 h-5 text-[#F7A06A]" />
        </div>

        {muerto ? (
          <div data-testid={estado === 'expired' ? 'pending-join-expired' : 'pending-join-invalid'}>
            <h3 className="text-[17px] font-semibold text-[#F4F4F3] mb-2 pr-10">
              {t('dashboard:groups.join_page_title')}
            </h3>
            <p className="text-[14px] leading-5 text-[#9A9A9A]">
              {estado === 'expired'
                ? t('dashboard:groups.join_expired')
                : t('dashboard:groups.join_invalid')}
            </p>
          </div>
        ) : (
          <div data-testid="pending-join-ready">
            {/* EL TITULO SIGUE AL HECHO: el del ya-miembro no dice que el grupo lo espera. */}
            <h3 className="text-[17px] font-semibold text-[#F4F4F3] mb-2 pr-10" data-testid="pending-join-title">
              {yaMiembro
                ? t('dashboard:groups.pending_already_member_title')
                : t('dashboard:groups.pending_title')}
            </h3>
            {yaMiembro && (
              <p className="text-[14px] leading-5 text-[#9A9A9A]">
                {t('dashboard:groups.pending_already_member')}
              </p>
            )}
            {/* PILOTO-1C: el nombre en UN renglon con puntos suspensivos (el tope real son 60
                caracteres). dashboard-rediseno (D5): dentro de su recuadro. */}
            <div className="mt-3 rounded-xl bg-[#0E0E0E] border border-[#2A2A2A] px-4 py-3 min-w-0">
              <p className="text-base font-semibold text-[#F4F4F3] truncate" data-testid="pending-join-group-name">
                {preview?.name}
              </p>
              {preview?.owner_name && (
                <p className="text-xs text-[#6a6a6a] mt-1 truncate" data-testid="pending-join-teacher">
                  {t('dashboard:groups.taught_by', { name: preview.owner_name })}
                </p>
              )}
            </div>
            {/* post-t7-2b (A1, #613): lo que vera el profe, debajo del nombre del grupo y antes del boton. Solo
                para quien se va a unir: el ya-miembro no acepta nada nuevo. */}
            {!yaMiembro && <AvisoAlUnirse className="mt-3" />}
          </div>
        )}
        </div>

        {/* El pie fijo. En el telefono el principal va arriba (primero en el arbol); desde 768 la fila
            se pinta al reves y queda a la derecha, despues de "Not now". */}
        <div className="shrink-0 pt-6" data-testid="pending-join-pie">
          {muerto ? (
            <div className="flex md:justify-end">
              <button
                type="button"
                onClick={ocultar}
                className={`${BOTON_VENTANA_PRINCIPAL} min-h-[44px]`}
                data-testid="pending-join-dismiss">
                {t('dashboard:groups.pending_open_btn')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row-reverse gap-2">
              <button
                type="button"
                onClick={yaMiembro ? () => { soltarPendiente(); ocultar(); } : unirse}
                disabled={estado === 'joining'}
                className={`${BOTON_VENTANA_PRINCIPAL} min-h-[44px]`}
                data-testid="pending-join-confirm">
                {estado === 'joining'
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : (<>{yaMiembro
                      ? t('dashboard:groups.pending_open_btn')
                      : <>{t('dashboard:groups.pending_confirm_btn')}<ArrowRight className="w-4 h-4" /></>}</>)}
              </button>
              {!yaMiembro && (
                <button
                  type="button"
                  onClick={cerrar}
                  className={`${BOTON_VENTANA_SECUNDARIO} min-h-[44px]`}
                  data-testid="pending-join-later">
                  {t('dashboard:groups.pending_close_btn')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
